<script setup lang="ts">
import { ref, computed } from 'vue';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type { CommonWageListRow, CommonWageTaxRate } from '@/types/commonWage';
import {
  getCommonWageLedgerGrossPay,
  calcCommonWageLedgerNetPay,
  getCommonWageOvertimeTotal,
  getCommonWageNightTotal,
  getCommonWageHolidayTotal,
  getCommonWagePremiumNontaxTotal,
  getCommonWagePremiumTaxTotal,
  sumCommonWagePayParts,
  isCommonWageAmountEqual,
  isCommonWageLaborTotalMismatch,
  isCommonWageCorpTotalMismatch,
  isCommonWageTaxableMismatch,
  dbCommonWageAmount,
  COMMON_WAGE_CLIENT_NONTAX_LABELS,
  applyCommonWageAccountingDisplay,
  previewCommonWageRetirement,
} from '@/policies/commonWageSettingPolicy';
import { compareJobThenName } from '@/policies/jobTypeSort';
import {
  taxRateInfoToRates,
  type IncomeTaxRow,
} from '@/composables/wage/useCommonWageCalc';
import { buildCommonWageAccountingReportRow } from '@/composables/wage/useCommonWageBulkSave';

const showModal = ref(false);
const isDownloading = ref(false);
const reportType = ref<'accounting' | 'client'>('accounting');

interface Props {
  year: number;
  month: number;
  wageData: CommonWageListRow[];
  memberName?: string;
  /** 회계용 공제 재산출용 — 없으면 지급칸 채움만 하고 공제는 저장값 */
  taxRateInfo?: CommonWageTaxRate | null;
  incomeTaxTable?: IncomeTaxRow[];
  /** 마감된 연월 — 공제·퇴직금은 저장값 유지 (회계용 중식 실비변상·생산직 비과세 표시는 적용) */
  snapshotOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taxRateInfo: null,
  incomeTaxTable: () => [],
  snapshotOnly: false,
});

const emit = defineEmits<{
  (e: 'error', message: string): void;
}>();

// 만나이 계산
const calculateAge = (birthDateStr: string | undefined, year: number, month: number) => {
  if (!birthDateStr) return '';
  const birthDate = new Date(birthDateStr);
  const baseDate = new Date(year, month - 1, 1);
  let age = baseDate.getFullYear() - birthDate.getFullYear();
  if (baseDate.getMonth() < birthDate.getMonth() || 
      (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age > 0 ? `만${age}세` : '';
};

function sortWageRows(rows: CommonWageListRow[]): CommonWageListRow[] {
  return [...rows].sort((a, b) =>
    compareJobThenName(getJobType(a), a.ce_name, getJobType(b), b.ce_name)
  );
}

const getJobType = (row: CommonWageListRow) => row.ce_job_type || row.ce_position || '';

const getBirthDateStr = (row: CommonWageListRow) => {
  const raw = row.cwhd_birth || row.ce_birth_date;
  if (!raw) return '';
  return String(raw).substring(0, 10);
};

// 숫자 포맷팅
const formatNumber = (num: number | undefined | null) => {
  const n = Number(num) || 0;
  return n.toLocaleString();
};

// 소수점 포맷팅 (정수면 소수점 제거)
/** 엑셀 합계 키 — 근로자/기관 국민연금처럼 라벨이 같아도 컬럼을 구분 */
const excelColTotalKey = (col: { field?: string | null; name: string }) =>
  col.field || col.name;

/** 시트 맨 위 제목 */
const wageLedgerTitle = () => {
  const facility = (props.memberName || '').trim();
  const period = `${props.year}년 ${props.month}월 임금대장`;
  return facility ? `${facility} ${period}` : period;
};

const addWageLedgerTitleRow = (worksheet: ExcelJS.Worksheet, title: string, colCount: number) => {
  const titleRow = worksheet.addRow([title]);
  titleRow.height = 32;
  const lastCol = Math.max(colCount, 1);
  for (let i = 1; i <= lastCol; i++) {
    const cell = titleRow.getCell(i);
    cell.font = { bold: true, size: 16, color: { argb: 'FF1e3a5f' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  }
  if (lastCol > 1) {
    worksheet.mergeCells(1, 1, 1, lastCol);
  }
};

const formatDecimal = (num: number | undefined | null) => {
  const n = Number(num) || 0;
  return n % 1 === 0 ? n.toFixed(0) : n.toString();
};

function toAccountingReportRow(row: CommonWageListRow): CommonWageListRow {
  const tax = props.taxRateInfo;
  const table = props.incomeTaxTable || [];
  if (!tax) {
    return applyCommonWageAccountingDisplay(row, { year: props.year, month: props.month });
  }
  return buildCommonWageAccountingReportRow(row, {
    taxRates: taxRateInfoToRates(tax),
    incomeTaxTable: table,
    year: props.year,
    month: props.month,
  });
}

/**
 * 열린 달 — 퇴직금은 한도 중식비 기준으로 고정하고, 기타공제·연말정산은 저장값(음수 포함)을 유지
 */
function applyOpenMonthAmounts(source: CommonWageListRow, view: CommonWageListRow): CommonWageListRow {
  const prevOut = dbCommonWageAmount(view.cwhd_c_out);
  const nextOut = previewCommonWageRetirement(source, props.year, props.month);
  const prevCorp = dbCommonWageAmount(view.cwhd_c_total);
  const prevEtc = dbCommonWageAmount(view.cwhd_l_etc);
  const nextEtc = dbCommonWageAmount(source.cwhd_l_etc);
  const prevYearend = dbCommonWageAmount(view.cwhd_l_yearend);
  const nextYearend = dbCommonWageAmount(source.cwhd_l_yearend);
  const prevLabor = dbCommonWageAmount(view.cwhd_l_total);
  return {
    ...view,
    cwhd_l_etc: nextEtc,
    cwhd_l_yearend: nextYearend,
    cwhd_l_total: prevLabor - prevEtc - prevYearend + nextEtc + nextYearend,
    cwhd_c_out: nextOut,
    cwhd_c_total: prevCorp - prevOut + nextOut,
  };
}

/**
 * 마감 달: 공제·퇴직금은 DB 저장값 유지.
 * 회계용만 중식 실비변상 한도 채움·생산직 비과세 칸을 표시에 반영
 * (DB에는 시설 입력 중식만 저장되므로 마감 후에도 채움을 건너뛰면 식대가 실비변상으로 안 보임)
 * 열린 달: 회계용은 공제까지 재산출, 기관용은 퇴직금만 재산출
 */
function reportRows(
  kind: 'accounting' | 'client',
  rows: CommonWageListRow[] | undefined
): CommonWageListRow[] {
  const list = rows || [];
  if (props.snapshotOnly) {
    if (kind === 'accounting') {
      return sortWageRows(
        list.map((row) =>
          applyCommonWageAccountingDisplay(row, { year: props.year, month: props.month })
        )
      );
    }
    return sortWageRows(list);
  }
  if (kind === 'accounting') {
    return sortWageRows(list.map((row) => applyOpenMonthAmounts(row, toAccountingReportRow(row))));
  }
  return sortWageRows(list.map((row) => applyOpenMonthAmounts(row, row)));
}

function rowsForCurrentReport(rows: CommonWageListRow[] | undefined): CommonWageListRow[] {
  return reportRows(reportType.value, rows);
}

// 정렬된 데이터
const sortedData = computed(() => rowsForCurrentReport(props.wageData));

// 합계 계산
const totals = computed(() => {
  const t = {
    base: 0, overtime: 0, overtimeNontax: 0, night: 0, nightNontax: 0,
    holiday: 0, holidayNontax: 0, premiumTax: 0, premiumNontax: 0, 
    overtimeTotal: 0, nightTotal: 0, holidayTotal: 0, // 고객용 합산
    dayoff: 0, longterm: 0, sunim: 0,
    bonus: 0, edu: 0, bojon: 0, etc: 0, dang: 0, lunch: 0, car: 0, childcare: 0,
    total: 0, taxable: 0, usual: 0,
    lYeon: 0, lGun: 0, lJang: 0, lGo: 0, lSo: 0, lJi: 0, lJiwon: 0, lSik: 0, lEtc: 0, lYearend: 0, lTotal: 0,
    cYeon: 0, cGun: 0, cJang: 0, cGo: 0, cSil: 0, cSan: 0, cOut: 0, cTotal: 0,
    netPay: 0
  };
  sortedData.value.forEach(row => {
    const gross = getCommonWageLedgerGrossPay(row);
    t.base += row.cwhd_basic || 0;
    t.overtime += row.cwhd_overtime || 0;
    t.overtimeNontax += row.cwhd_overtime_nontax || 0;
    t.night += row.cwhd_night || 0;
    t.nightNontax += row.cwhd_night_nontax || 0;
    t.holiday += row.cwhd_holiday || 0;
    t.holidayNontax += row.cwhd_holiday_nontax || 0;
    t.premiumTax += getCommonWagePremiumTaxTotal(row);
    t.premiumNontax += getCommonWagePremiumNontaxTotal(row);
    // 고객용 합산 합계
    t.overtimeTotal += getCommonWageOvertimeTotal(row);
    t.nightTotal += getCommonWageNightTotal(row);
    t.holidayTotal += getCommonWageHolidayTotal(row);
    t.dayoff += row.cwhd_dayoff || 0;
    t.longterm += row.cwhd_senior || 0;
    t.sunim += row.cwhd_sunim || 0;
    t.bonus += row.cwhd_bonus || 0;
    t.edu += row.cwhd_bosu || 0;
    t.bojon += row.cwhd_bojon || 0;
    t.etc += row.cwhd_other || 0;
    t.dang += row.cwhd_dang || 0;
    t.lunch += row.cwhd_lunch || 0;
    t.car += row.cwhd_car || 0;
    t.childcare += row.cwhd_child || 0;
    t.total += gross;
    t.taxable += row.cwhd_taxable || 0;
    t.usual += row.cwhd_usual || 0;
    t.lYeon += row.cwhd_l_yeon || 0;
    t.lGun += row.cwhd_l_gun || 0;
    t.lJang += row.cwhd_l_jang || 0;
    t.lGo += row.cwhd_l_go || 0;
    t.lSo += row.cwhd_l_so || 0;
    t.lJi += row.cwhd_l_ji || 0;
    t.lJiwon += row.cwhd_l_jiwon || 0;
    t.lSik += row.cwhd_l_sik || 0;
    t.lEtc += row.cwhd_l_etc || 0;
    t.lYearend += row.cwhd_l_yearend || 0;
    t.lTotal += row.cwhd_l_total || 0;
    t.cYeon += row.cwhd_c_yeon || 0;
    t.cGun += row.cwhd_c_gun || 0;
    t.cJang += row.cwhd_c_jang || 0;
    t.cGo += row.cwhd_c_go || 0;
    t.cSil += row.cwhd_c_sil || 0;
    t.cSan += row.cwhd_c_san || 0;
    t.cOut += row.cwhd_c_out || 0;
    t.cTotal += row.cwhd_c_total || 0;
    t.netPay += calcCommonWageLedgerNetPay(row, gross);
  });
  return t;
});

/** 0이 아닌 금액 — 환급(음수) 기타공제·연말정산도 칸을 연다 */
const amountPresent = (value: unknown): boolean => {
  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
};

const hasNonZero = (field: keyof CommonWageListRow): boolean => {
  return sortedData.value.some(row => amountPresent(row[field]));
};

// 미리보기 컬럼 표시 여부 (0원인 항목 숨김)
const showColumns = computed(() => ({
  // 임금구성
  premiumTax:
    hasNonZero('cwhd_overtime') ||
    hasNonZero('cwhd_night') ||
    hasNonZero('cwhd_holiday'),
  premiumNontax:
    hasNonZero('cwhd_overtime_nontax') ||
    hasNonZero('cwhd_night_nontax') ||
    hasNonZero('cwhd_holiday_nontax'),
  dayoff: hasNonZero('cwhd_dayoff'),
  longterm: hasNonZero('cwhd_senior'),
  sunim: hasNonZero('cwhd_sunim'),
  bonus: hasNonZero('cwhd_bonus'),
  edu: hasNonZero('cwhd_bosu'),
  bojon: hasNonZero('cwhd_bojon'),
  etc: hasNonZero('cwhd_other'),
  dang: hasNonZero('cwhd_dang'),
  lunch: hasNonZero('cwhd_lunch'),
  car: hasNonZero('cwhd_car'),
  childcare: hasNonZero('cwhd_child'),
  // 근로자공제
  lYeon: hasNonZero('cwhd_l_yeon'),
  lGun: hasNonZero('cwhd_l_gun'),
  lJang: hasNonZero('cwhd_l_jang'),
  lGo: hasNonZero('cwhd_l_go'),
  lSo: hasNonZero('cwhd_l_so'),
  lJi: hasNonZero('cwhd_l_ji'),
  lJiwon: hasNonZero('cwhd_l_jiwon'),
  lSik: hasNonZero('cwhd_l_sik'),
  lEtc: hasNonZero('cwhd_l_etc'),
  lYearend: hasNonZero('cwhd_l_yearend'),
  // 기관공제
  cYeon: hasNonZero('cwhd_c_yeon'),
  cGun: hasNonZero('cwhd_c_gun'),
  cJang: hasNonZero('cwhd_c_jang'),
  cGo: hasNonZero('cwhd_c_go'),
  cSil: hasNonZero('cwhd_c_sil'),
  cSan: hasNonZero('cwhd_c_san'),
  cOut: hasNonZero('cwhd_c_out'),
}));

// 고객용: 연장/야간/휴일 합산 표시 여부
const showClientColumns = computed(() => ({
  overtimeTotal: props.wageData?.some(r => getCommonWageOvertimeTotal(r) > 0) ?? false,
  nightTotal: props.wageData?.some(r => getCommonWageNightTotal(r) > 0) ?? false,
  holidayTotal: props.wageData?.some(r => getCommonWageHolidayTotal(r) > 0) ?? false,
}));

// 고객용 합산 값 계산 (정책 단일 소스)
const getOvertimeTotal = (row: CommonWageListRow) => getCommonWageOvertimeTotal(row);
const getNightTotal = (row: CommonWageListRow) => getCommonWageNightTotal(row);
const getHolidayTotal = (row: CommonWageListRow) => getCommonWageHolidayTotal(row);
const getRowGrossPay = (row: CommonWageListRow) => getCommonWageLedgerGrossPay(row);
const getRowNetPay = (row: CommonWageListRow) => calcCommonWageLedgerNetPay(row);

/** 미리보기 — 합산 불일치 시 빨간 숫자 (신고용·기관확인용 공통) */
const isAccountingPreview = () => reportType.value === 'accounting';

/** 화면 항목 합 ≠ 임금합계 칸 (DB 저장값과 비교하지 않음 — 레거시 저장 불일치 오탐 방지) */
const rowWageMismatch = (row: CommonWageListRow) => {
  const parts = sumCommonWagePayParts(row);
  const displayed = getRowGrossPay(row);
  if (parts === 0 && displayed === 0) return false;
  return !isCommonWageAmountEqual(parts, displayed);
};
const rowLaborMismatch = (row: CommonWageListRow) => isCommonWageLaborTotalMismatch(row);
const rowCorpMismatch = (row: CommonWageListRow) => isCommonWageCorpTotalMismatch(row);
const rowTaxableMismatch = (row: CommonWageListRow) =>
  isAccountingPreview() && isCommonWageTaxableMismatch(row);

/** 하단 합계행 — 항목 세로합 vs 합계컬럼(표시값) */
const footerMismatch = computed(() => {
  const t = totals.value;
  const isClient = reportType.value === 'client';
  const wageParts = isClient
    ? t.base +
      t.overtimeTotal +
      t.nightTotal +
      t.holidayTotal +
      t.dayoff +
      t.longterm +
      t.sunim +
      t.bonus +
      t.edu +
      t.bojon +
      t.etc +
      t.dang +
      t.lunch +
      t.car +
      t.childcare
    : t.base +
      t.overtime +
      t.overtimeNontax +
      t.night +
      t.nightNontax +
      t.holiday +
      t.holidayNontax +
      t.dayoff +
      t.longterm +
      t.sunim +
      t.bonus +
      t.edu +
      t.bojon +
      t.etc +
      t.dang +
      t.lunch +
      t.car +
      t.childcare;
  const laborParts =
    t.lYeon + t.lGun + t.lJang + t.lGo + t.lSo + t.lJi + t.lJiwon + t.lSik + t.lEtc + t.lYearend;
  const corpParts = t.cYeon + t.cGun + t.cJang + t.cGo + t.cSil + t.cSan + t.cOut;
  const dbTaxableSum = sortedData.value.reduce((s, r) => s + dbCommonWageAmount(r.cwhd_taxable), 0);
  const anyRowWage = sortedData.value.some((r) => rowWageMismatch(r));
  const anyRowLabor = sortedData.value.some((r) => isCommonWageLaborTotalMismatch(r));
  const anyRowCorp = sortedData.value.some((r) => isCommonWageCorpTotalMismatch(r));
  const anyRowTaxable =
    isAccountingPreview() && sortedData.value.some((r) => isCommonWageTaxableMismatch(r));

  return {
    wage: anyRowWage || !isCommonWageAmountEqual(wageParts, t.total),
    labor: anyRowLabor || !isCommonWageAmountEqual(laborParts, t.lTotal),
    corp: anyRowCorp || !isCommonWageAmountEqual(corpParts, t.cTotal),
    taxable:
      isAccountingPreview() &&
      (anyRowTaxable || !isCommonWageAmountEqual(dbTaxableSum, t.taxable)),
  };
});

// 동적 colspan 계산
const wageColspan = computed(() => {
  let count = 2; // 기본급, 임금합계 (필수)
  const s = showColumns.value;
  const sc = showClientColumns.value;
  
  if (reportType.value === 'client') {
    // 고객용: 합산 컬럼
    if (sc.overtimeTotal) count++;
    if (sc.nightTotal) count++;
    if (sc.holidayTotal) count++;
  } else {
    // 회계처리용: 연야휴과세 + 연야휴비과세
    if (s.premiumTax) count++;
    if (s.premiumNontax) count++;
  }
  if (s.dayoff) count++;
  if (s.longterm) count++;
  if (s.sunim) count++;
  if (s.bonus) count++;
  if (s.edu) count++;
  if (s.bojon) count++;
  if (s.etc) count++;
  if (s.dang) count++;
  if (s.lunch) count++;
  if (s.car) count++;
  if (s.childcare) count++;
  return count;
});

const empDeductColspan = computed(() => {
  let count = 1; // 공제합계 (필수)
  const s = showColumns.value;
  if (s.lYeon) count++;
  if (s.lGun) count++;
  if (s.lJang) count++;
  if (s.lGo) count++;
  if (s.lSo) count++;
  if (s.lJi) count++;
  if (s.lJiwon) count++;
  if (s.lSik) count++;
  if (s.lEtc) count++;
  if (s.lYearend) count++;
  return count;
});

const corpDeductColspan = computed(() => {
  let count = 1; // 기관합계 (필수)
  const s = showColumns.value;
  if (s.cYeon) count++;
  if (s.cGun) count++;
  if (s.cJang) count++;
  if (s.cGo) count++;
  if (s.cSil) count++;
  if (s.cSan) count++;
  if (s.cOut) count++;
  return count;
});

// 미리보기 열기 (회계처리용 또는 고객용)
const openPreview = (type: 'accounting' | 'client' = 'accounting') => {
  reportType.value = type;
  showModal.value = true;
};

// 미리보기 닫기
const closePreview = () => {
  showModal.value = false;
};

// 현재 선택된 타입에 따라 다운로드
const handleDownload = () => {
  if (reportType.value === 'client') {
    downloadClientReport();
  } else {
    downloadPayrollReport();
  }
};

// 임금대장 엑셀 다운로드
const downloadPayrollReport = async () => {
  if (!props.wageData || props.wageData.length === 0) {
    emit('error', '다운로드할 데이터가 없습니다.');
    return;
  }

  isDownloading.value = true;
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('임금대장');

    // 컬럼 정의 (그룹, 컬럼명, 필드명, 필수여부)
    type ColDef = { group: string; name: string; field: string | null; required?: boolean };
    const allColumns: ColDef[] = [
      // 기본정보 (필수)
      { group: 'basic', name: '순번', field: null, required: true },
      { group: 'basic', name: '성명', field: null, required: true },
      { group: 'basic', name: '생년월일', field: null, required: true },
      { group: 'basic', name: '직종', field: null, required: true },
      // 근로정보 (필수)
      { group: 'work', name: '시급', field: 'cwhd_pay_calc_hourly', required: true },
      { group: 'work', name: '일수', field: 'cwhd_work_days', required: true },
      { group: 'work', name: '시간', field: 'cwhd_work_hours', required: true },
      // 임금구성
      { group: 'wage', name: '기본급', field: 'cwhd_basic', required: true },
      { group: 'wage', name: '연야휴과세', field: '_premium_tax' },
      { group: 'wage', name: '연야휴비과세', field: '_premium_nontax' },
      { group: 'wage', name: '연차', field: 'cwhd_dayoff' },
      { group: 'wage', name: '장기', field: 'cwhd_senior' },
      { group: 'wage', name: '선임', field: 'cwhd_sunim' },
      { group: 'wage', name: '상여', field: 'cwhd_bonus' },
      { group: 'wage', name: '교육', field: 'cwhd_bosu' },
      { group: 'wage', name: '보존', field: 'cwhd_bojon' },
      { group: 'wage', name: '기타', field: 'cwhd_other' },
      { group: 'wage', name: '당직', field: 'cwhd_dang' },
      { group: 'wage', name: '중식비(비과세)', field: 'cwhd_lunch' },
      { group: 'wage', name: '차량(비과세)', field: 'cwhd_car' },
      { group: 'wage', name: '양육(비과세)', field: 'cwhd_child' },
      { group: 'wage', name: '임금합계', field: '_gross_pay', required: true },
      // 조정내역
      { group: 'adjust', name: '과세', field: 'cwhd_taxable', required: true },
      { group: 'adjust', name: '통상', field: 'cwhd_usual', required: true },
      // 근로자공제
      { group: 'empDeduct', name: '국민연금', field: 'cwhd_l_yeon' },
      { group: 'empDeduct', name: '건강보험', field: 'cwhd_l_gun' },
      { group: 'empDeduct', name: '장기요양', field: 'cwhd_l_jang' },
      { group: 'empDeduct', name: '고용보험', field: 'cwhd_l_go' },
      { group: 'empDeduct', name: '소득세', field: 'cwhd_l_so' },
      { group: 'empDeduct', name: '지방세', field: 'cwhd_l_ji' },
      { group: 'empDeduct', name: '지원공제', field: 'cwhd_l_jiwon' },
      { group: 'empDeduct', name: '식비공제', field: 'cwhd_l_sik' },
      { group: 'empDeduct', name: '기타공제', field: 'cwhd_l_etc' },
      { group: 'empDeduct', name: '연말정산', field: 'cwhd_l_yearend' },
      { group: 'empDeduct', name: '근로자공제합계', field: 'cwhd_l_total', required: true },
      { group: 'net', name: '실수령액', field: null, required: true },
      // 기관공제
      { group: 'corpDeduct', name: '국민연금', field: 'cwhd_c_yeon' },
      { group: 'corpDeduct', name: '건강보험', field: 'cwhd_c_gun' },
      { group: 'corpDeduct', name: '장기요양', field: 'cwhd_c_jang' },
      { group: 'corpDeduct', name: '고용보험', field: 'cwhd_c_go' },
      { group: 'corpDeduct', name: '실업급여', field: 'cwhd_c_sil' },
      { group: 'corpDeduct', name: '산재보험', field: 'cwhd_c_san' },
      { group: 'corpDeduct', name: '퇴직금', field: 'cwhd_c_out' },
      { group: 'corpDeduct', name: '기관공제합계', field: 'cwhd_c_total', required: true },
      { group: 'remark', name: '비고', field: null, required: true }
    ];

    const getAccountingFieldValue = (row: CommonWageListRow, field: string | null): number => {
      if (!field) return 0;
      if (field === '_gross_pay') return getRowGrossPay(row);
      if (field === '_premium_tax') return getCommonWagePremiumTaxTotal(row);
      if (field === '_premium_nontax') return getCommonWagePremiumNontaxTotal(row);
      return Number((row as any)[field]) || 0;
    };

    // 각 컬럼별 합계 계산하여 0인 컬럼 필터링
    const payrollRows = reportRows('accounting', props.wageData);

    const hasNonZeroValue = (field: string | null): boolean => {
      if (!field) return true;
      return payrollRows.some(row => amountPresent(getAccountingFieldValue(row, field)));
    };

    // 필수이거나 0이 아닌 값이 있는 컬럼만 포함
    const activeColumns = allColumns.filter(col => col.required || hasNonZeroValue(col.field));

    // 2단 헤더 생성
    const header2Columns = activeColumns.map(col => col.name);

    // 그룹별 시작/끝 인덱스 계산
    const getGroupRange = (groupName: string) => {
      const cols = activeColumns.map((c, i) => ({ ...c, idx: i + 1 })).filter(c => c.group === groupName);
      if (cols.length === 0) return null;
      return { start: cols[0].idx, end: cols[cols.length - 1].idx };
    };

    const basicRange = getGroupRange('basic');
    const workRange = getGroupRange('work');
    const wageRange = getGroupRange('wage');
    const adjustRange = getGroupRange('adjust');
    const empDeductRange = getGroupRange('empDeduct');
    const corpDeductRange = getGroupRange('corpDeduct');
    const netRange = getGroupRange('net');
    const remarkRange = getGroupRange('remark');

    addWageLedgerTitleRow(worksheet, wageLedgerTitle(), header2Columns.length);

    // 1단 헤더 행 추가 (제목행이 1행)
    const header1Row = worksheet.addRow([]);
    if (basicRange) header1Row.getCell(basicRange.start).value = '기본정보';
    if (workRange) header1Row.getCell(workRange.start).value = '근로정보';
    if (wageRange) header1Row.getCell(wageRange.start).value = '임금구성';
    if (adjustRange) header1Row.getCell(adjustRange.start).value = '조정내역';
    if (empDeductRange) header1Row.getCell(empDeductRange.start).value = '근로자공제';
    if (corpDeductRange) header1Row.getCell(corpDeductRange.start).value = '기관공제';
    if (netRange) header1Row.getCell(netRange.start).value = '수령';
    if (remarkRange) header1Row.getCell(remarkRange.start).value = '비고';

    // 1단 헤더 병합
    if (basicRange && basicRange.end > basicRange.start) worksheet.mergeCells(2, basicRange.start, 2, basicRange.end);
    if (workRange && workRange.end > workRange.start) worksheet.mergeCells(2, workRange.start, 2, workRange.end);
    if (wageRange && wageRange.end > wageRange.start) worksheet.mergeCells(2, wageRange.start, 2, wageRange.end);
    if (adjustRange && adjustRange.end > adjustRange.start) worksheet.mergeCells(2, adjustRange.start, 2, adjustRange.end);
    if (empDeductRange && empDeductRange.end > empDeductRange.start) worksheet.mergeCells(2, empDeductRange.start, 2, empDeductRange.end);
    if (corpDeductRange && corpDeductRange.end > corpDeductRange.start) worksheet.mergeCells(2, corpDeductRange.start, 2, corpDeductRange.end);

    // 1단 헤더 스타일
    for (let i = 1; i <= header2Columns.length; i++) {
      const cell = header1Row.getCell(i);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1e3a5f' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }

    // 비과세 컬럼 인덱스 찾기 (동적)
    const nontaxColIndices: number[] = [];
    activeColumns.forEach((col, idx) => {
      if (col.name.includes('비과세')) nontaxColIndices.push(idx + 1);
    });

    // 2단 헤더 행 추가
    const header2Row = worksheet.addRow(header2Columns);
    header2Row.eachCell((cell, colNumber) => {
      let bgColor = 'FFD9E1F2';  // 기본색
      if (wageRange && colNumber >= wageRange.start && colNumber <= wageRange.end) bgColor = 'FFE2EFDA';  // 임금구성 연두색
      if (empDeductRange && colNumber >= empDeductRange.start && colNumber <= empDeductRange.end) bgColor = 'FFFCE4D6';  // 근로자공제 살구색
      if (corpDeductRange && colNumber >= corpDeductRange.start && colNumber <= corpDeductRange.end) bgColor = 'FFFFF2CC';  // 기관공제 노랑
      // 비과세 컬럼 파랑 (동적 인덱스)
      if (nontaxColIndices.includes(colNumber)) bgColor = 'FFE0F2FE';
      
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // 열 너비 설정
    worksheet.getColumn(1).width = 6;   // 순번
    worksheet.getColumn(2).width = 10;  // 성명
    worksheet.getColumn(3).width = 12;  // 생년월일
    worksheet.getColumn(4).width = 12;  // 직종
    for (let i = 5; i <= header2Columns.length; i++) {
      const colName = header2Columns[i - 1];
      if (colName.includes('합계') || colName === '실수령액') worksheet.getColumn(i).width = 13;
      else if (colName === '비고') worksheet.getColumn(i).width = 10;
      else if (colName === '기본급' || colName === '과세' || colName === '통상' || colName === '연야휴과세' || colName === '연야휴비과세') worksheet.getColumn(i).width = 12;
      else worksheet.getColumn(i).width = 10;
    }

    // 합계 변수
    const totals: Record<string, number> = {};
    activeColumns.forEach(col => { 
      if (col.field || col.name === '실수령액') totals[excelColTotalKey(col)] = 0; 
    });

    // 데이터 행 추가
    payrollRows.forEach((row, index) => {
      const grossPay = getRowGrossPay(row);
      const netPay = getRowNetPay(row);
      const remarkText = calculateAge(getBirthDateStr(row), props.year, props.month);

      // activeColumns 기반 동적 데이터 생성
      const rowData = activeColumns.map((col, colIdx) => {
        if (col.name === '순번') return index + 1;
        if (col.name === '성명') return row.ce_name || '';
        if (col.name === '생년월일') return getBirthDateStr(row);
        if (col.name === '직종') return getJobType(row);
        if (col.name === '실수령액') return netPay;
        if (col.name === '비고') return remarkText;
        if (col.field) return getAccountingFieldValue(row, col.field);
        return '';
      });

      const dataRow = worksheet.addRow(rowData);
      
      // 데이터 행 스타일
      dataRow.eachCell((cell, colNumber) => {
        const col = activeColumns[colNumber - 1];
        if (col && col.group === 'basic') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          const isNontaxCol = nontaxColIndices.includes(colNumber);
          let bgColor = 'FFFFFFFF';
          if (isNontaxCol) bgColor = 'FFE0F2FE';
          
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
        }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
        };
      });

      // 합계 누적 (숫자 컬럼만)
      activeColumns.forEach((col, idx) => {
        if ((col.field || col.name === '실수령액') && idx >= 4) {
          const val = rowData[idx];
          if (typeof val === 'number') {
            const key = excelColTotalKey(col);
            totals[key] = (totals[key] || 0) + val;
          }
        }
      });
    });

    // 합계 행 (동적 컬럼 기반)
    const totalRowData = activeColumns.map((col, idx) => {
      if (col.name === '순번') return '';
      if (col.name === '성명') return '합계';
      if (col.name === '생년월일' || col.name === '직종' || col.name === '비고') return '';
      return totals[excelColTotalKey(col)] || 0;
    });

    const totalRow = worksheet.addRow(totalRowData);
    const basicEnd = basicRange?.end || 4;
    const remarkStart = remarkRange?.start || activeColumns.length;
    totalRow.eachCell((cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: colNumber <= basicEnd ? 'center' : 'right', vertical: 'middle' };
      cell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      if (colNumber > basicEnd && colNumber < remarkStart) {
        cell.numFmt = '#,##0';
      }
    });

    // 파일 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `회계처리용_임금대장_${props.year}년${String(props.month).padStart(2, '0')}월.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('엑셀 다운로드 오류:', error);
    emit('error', '임금대장 다운로드 중 오류가 발생했습니다.');
  } finally {
    isDownloading.value = false;
  }
};

// 고객용 임금대장 엑셀 다운로드 (과세/비과세 구분 없이, 조정내역 제외)
const downloadClientReport = async () => {
  if (!props.wageData || props.wageData.length === 0) {
    emit('error', '다운로드할 데이터가 없습니다.');
    return;
  }

  isDownloading.value = true;
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('임금대장');

    // 고객용 컬럼 정의 (과세/비과세 합산, 조정내역 제외)
    type ColDef = { group: string; name: string; field: string | null; required?: boolean };
    const allColumns: ColDef[] = [
      // 기본정보 (필수)
      { group: 'basic', name: '순번', field: null, required: true },
      { group: 'basic', name: '성명', field: null, required: true },
      { group: 'basic', name: '생년월일', field: null, required: true },
      { group: 'basic', name: '직종', field: null, required: true },
      // 근로정보 (필수)
      { group: 'work', name: '시급', field: 'cwhd_pay_calc_hourly', required: true },
      { group: 'work', name: '일수', field: 'cwhd_work_days', required: true },
      { group: 'work', name: '시간', field: 'cwhd_work_hours', required: true },
      // 임금구성 (과세+비과세 합산)
      { group: 'wage', name: '기본급', field: 'cwhd_basic', required: true },
      { group: 'wage', name: '연장수당', field: '_overtime_total' },
      { group: 'wage', name: '야간수당', field: '_night_total' },
      { group: 'wage', name: '휴일수당', field: '_holiday_total' },
      { group: 'wage', name: '연차수당', field: 'cwhd_dayoff' },
      { group: 'wage', name: '장기근속', field: 'cwhd_senior' },
      { group: 'wage', name: '선임수당', field: 'cwhd_sunim' },
      { group: 'wage', name: '상여금', field: 'cwhd_bonus' },
      { group: 'wage', name: '교육비', field: 'cwhd_bosu' },
      { group: 'wage', name: '보존수당', field: 'cwhd_bojon' },
      { group: 'wage', name: '기타수당', field: 'cwhd_other' },
      { group: 'wage', name: '당직수당', field: 'cwhd_dang' },
      { group: 'wage', name: COMMON_WAGE_CLIENT_NONTAX_LABELS.lunch, field: 'cwhd_lunch' },
      { group: 'wage', name: COMMON_WAGE_CLIENT_NONTAX_LABELS.car, field: 'cwhd_car' },
      { group: 'wage', name: COMMON_WAGE_CLIENT_NONTAX_LABELS.child, field: 'cwhd_child' },
      { group: 'wage', name: '임금합계', field: '_gross_pay', required: true },
      // 근로자공제
      { group: 'empDeduct', name: '국민연금', field: 'cwhd_l_yeon' },
      { group: 'empDeduct', name: '건강보험', field: 'cwhd_l_gun' },
      { group: 'empDeduct', name: '장기요양', field: 'cwhd_l_jang' },
      { group: 'empDeduct', name: '고용보험', field: 'cwhd_l_go' },
      { group: 'empDeduct', name: '소득세', field: 'cwhd_l_so' },
      { group: 'empDeduct', name: '지방세', field: 'cwhd_l_ji' },
      { group: 'empDeduct', name: '지원공제', field: 'cwhd_l_jiwon' },
      { group: 'empDeduct', name: '식비공제', field: 'cwhd_l_sik' },
      { group: 'empDeduct', name: '기타공제', field: 'cwhd_l_etc' },
      { group: 'empDeduct', name: '연말정산', field: 'cwhd_l_yearend' },
      { group: 'empDeduct', name: '근로자공제합계', field: 'cwhd_l_total', required: true },
      { group: 'net', name: '실수령액', field: null, required: true },
      // 기관공제
      { group: 'corpDeduct', name: '국민연금', field: 'cwhd_c_yeon' },
      { group: 'corpDeduct', name: '건강보험', field: 'cwhd_c_gun' },
      { group: 'corpDeduct', name: '장기요양', field: 'cwhd_c_jang' },
      { group: 'corpDeduct', name: '고용보험', field: 'cwhd_c_go' },
      { group: 'corpDeduct', name: '실업급여', field: 'cwhd_c_sil' },
      { group: 'corpDeduct', name: '산재보험', field: 'cwhd_c_san' },
      { group: 'corpDeduct', name: '퇴직금', field: 'cwhd_c_out' },
      { group: 'corpDeduct', name: '기관공제합계', field: 'cwhd_c_total', required: true },
      { group: 'remark', name: '비고', field: null, required: true }
    ];

    // 합산 값 계산 헬퍼 (정책 단일 소스)
    const getFieldValue = (row: CommonWageListRow, field: string | null): number => {
      if (!field) return 0;
      if (field === '_overtime_total') return getCommonWageOvertimeTotal(row);
      if (field === '_night_total') return getCommonWageNightTotal(row);
      if (field === '_holiday_total') return getCommonWageHolidayTotal(row);
      if (field === '_gross_pay') return getRowGrossPay(row);
      return Number((row as any)[field]) || 0;
    };

    // 각 컬럼별 합계 계산하여 0인 컬럼 필터링
    const clientRows = reportRows('client', props.wageData);
    const hasNonZeroValue = (field: string | null): boolean => {
      if (!field) return true;
      return clientRows.some(row => amountPresent(getFieldValue(row, field)));
    };

    // 필수이거나 0이 아닌 값이 있는 컬럼만 포함
    const activeColumns = allColumns.filter(col => col.required || hasNonZeroValue(col.field));

    // 2단 헤더 생성
    const header2Columns = activeColumns.map(col => col.name);

    // 그룹별 시작/끝 인덱스 계산
    const getGroupRange = (groupName: string) => {
      const cols = activeColumns.map((c, i) => ({ ...c, idx: i + 1 })).filter(c => c.group === groupName);
      if (cols.length === 0) return null;
      return { start: cols[0].idx, end: cols[cols.length - 1].idx };
    };

    const basicRange = getGroupRange('basic');
    const workRange = getGroupRange('work');
    const wageRange = getGroupRange('wage');
    const empDeductRange = getGroupRange('empDeduct');
    const corpDeductRange = getGroupRange('corpDeduct');
    const netRange = getGroupRange('net');
    const remarkRange = getGroupRange('remark');

    addWageLedgerTitleRow(worksheet, wageLedgerTitle(), header2Columns.length);

    // 1단 헤더 행 추가 (제목행이 1행)
    const header1Row = worksheet.addRow([]);
    if (basicRange) header1Row.getCell(basicRange.start).value = '기본정보';
    if (workRange) header1Row.getCell(workRange.start).value = '근로정보';
    if (wageRange) header1Row.getCell(wageRange.start).value = '임금구성';
    if (empDeductRange) header1Row.getCell(empDeductRange.start).value = '근로자공제';
    if (corpDeductRange) header1Row.getCell(corpDeductRange.start).value = '기관공제';
    if (netRange) header1Row.getCell(netRange.start).value = '수령';
    if (remarkRange) header1Row.getCell(remarkRange.start).value = '비고';

    // 1단 헤더 병합
    if (basicRange && basicRange.end > basicRange.start) worksheet.mergeCells(2, basicRange.start, 2, basicRange.end);
    if (workRange && workRange.end > workRange.start) worksheet.mergeCells(2, workRange.start, 2, workRange.end);
    if (wageRange && wageRange.end > wageRange.start) worksheet.mergeCells(2, wageRange.start, 2, wageRange.end);
    if (empDeductRange && empDeductRange.end > empDeductRange.start) worksheet.mergeCells(2, empDeductRange.start, 2, empDeductRange.end);
    if (corpDeductRange && corpDeductRange.end > corpDeductRange.start) worksheet.mergeCells(2, corpDeductRange.start, 2, corpDeductRange.end);

    // 1단 헤더 스타일
    for (let i = 1; i <= header2Columns.length; i++) {
      const cell = header1Row.getCell(i);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1e3a5f' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }

    // 2단 헤더 행 추가
    const header2Row = worksheet.addRow(header2Columns);
    header2Row.eachCell((cell, colNumber) => {
      let bgColor = 'FFD9E1F2';
      if (wageRange && colNumber >= wageRange.start && colNumber <= wageRange.end) bgColor = 'FFE2EFDA';
      if (empDeductRange && colNumber >= empDeductRange.start && colNumber <= empDeductRange.end) bgColor = 'FFFCE4D6';
      if (corpDeductRange && colNumber >= corpDeductRange.start && colNumber <= corpDeductRange.end) bgColor = 'FFFFF2CC';
      
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // 열 너비 설정
    worksheet.getColumn(1).width = 6;
    worksheet.getColumn(2).width = 10;
    worksheet.getColumn(3).width = 12;
    worksheet.getColumn(4).width = 12;
    for (let i = 5; i <= header2Columns.length; i++) {
      const colName = header2Columns[i - 1];
      if (colName.includes('합계') || colName === '실수령액') worksheet.getColumn(i).width = 13;
      else if (colName === '비고') worksheet.getColumn(i).width = 10;
      else if (colName === '기본급') worksheet.getColumn(i).width = 12;
      else worksheet.getColumn(i).width = 10;
    }

    // 합계 변수
    const totals: Record<string, number> = {};
    activeColumns.forEach(col => { 
      if (col.field || col.name === '실수령액') totals[excelColTotalKey(col)] = 0; 
    });

    const sortedData = reportRows('client', props.wageData);

    // 데이터 행 추가
    sortedData.forEach((row, index) => {
      const netPay = getRowNetPay(row);
      const remarkText = calculateAge(getBirthDateStr(row), props.year, props.month);

      const rowData = activeColumns.map((col) => {
        if (col.name === '순번') return index + 1;
        if (col.name === '성명') return row.ce_name || '';
        if (col.name === '생년월일') return getBirthDateStr(row);
        if (col.name === '직종') return getJobType(row);
        if (col.name === '실수령액') return netPay;
        if (col.name === '비고') return remarkText;
        if (col.field) return getFieldValue(row, col.field);
        return '';
      });

      const dataRow = worksheet.addRow(rowData);
      const basicEnd = basicRange?.end || 4;
      const remarkStart = remarkRange?.start || activeColumns.length;
      
      dataRow.eachCell((cell, colNumber) => {
        const col = activeColumns[colNumber - 1];
        cell.alignment = { horizontal: colNumber <= basicEnd ? 'center' : 'right', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        if (colNumber > basicEnd && colNumber < remarkStart) {
          cell.numFmt = '#,##0';
        }
        // 합계 누적
        if (col.field) {
          const key = excelColTotalKey(col);
          totals[key] = (totals[key] || 0) + getFieldValue(row, col.field);
        }
        if (col.name === '실수령액') totals['실수령액'] = (totals['실수령액'] || 0) + netPay;
      });
    });

    // 합계 행
    const totalRowData = activeColumns.map((col) => {
      if (col.name === '순번') return '';
      if (col.name === '성명') return '합계';
      if (col.name === '생년월일' || col.name === '직종' || col.name === '비고') return '';
      return totals[excelColTotalKey(col)] || 0;
    });

    const totalRow = worksheet.addRow(totalRowData);
    const basicEnd = basicRange?.end || 4;
    const remarkStart = remarkRange?.start || activeColumns.length;
    totalRow.eachCell((cell, colNumber) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
      cell.font = { bold: true, size: 10 };
      cell.alignment = { horizontal: colNumber <= basicEnd ? 'center' : 'right', vertical: 'middle' };
      cell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      if (colNumber > basicEnd && colNumber < remarkStart) {
        cell.numFmt = '#,##0';
      }
    });

    // 파일 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `임금대장_${props.year}년${String(props.month).padStart(2, '0')}월.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('고객용 임금대장 다운로드 오류:', error);
    emit('error', '임금대장 다운로드 중 오류가 발생했습니다.');
  } finally {
    isDownloading.value = false;
  }
};

defineExpose({
  downloadPayrollReport,
  downloadClientReport,
  openPreview
});
</script>

<template>
  <Teleport to="body">
    <div v-if="showModal" class="wage-report-modal-overlay" @click.self="closePreview">
      <div class="wage-report-modal">
        <div class="modal-header">
          <h3>
            {{ reportType === 'accounting' ? '회계처리용 ' : '' }}임금대장 미리보기 ({{ year }}년 {{ month }}월)
          </h3>
          <div class="header-actions">
            <button class="download-btn" @click="handleDownload" :disabled="isDownloading">
              <v-icon v-if="isDownloading" size="16" class="spin">mdi-loading</v-icon>
              <v-icon v-else size="16">mdi-download</v-icon>
              {{ isDownloading ? '다운로드 중...' : '엑셀 다운로드' }}
            </button>
            <button class="close-btn" @click="closePreview">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="modal-body">
          <div class="table-wrapper">
            <table class="wage-report-table">
              <thead>
                <tr class="header-row-1">
                  <th colspan="4" class="section-basic">기본정보</th>
                  <th colspan="3" class="section-work">근로정보</th>
                  <th :colspan="wageColspan" class="section-wage">임금구성</th>
                  <th v-if="reportType === 'accounting'" colspan="2" class="section-adjust">조정내역</th>
                  <th :colspan="empDeductColspan" class="section-emp-deduct">근로자공제</th>
                  <th class="section-net">수령</th>
                  <th :colspan="corpDeductColspan" class="section-center-deduct">기관공제</th>
                </tr>
                <tr class="header-row-2">
                  <th>순번</th>
                  <th>성명</th>
                  <th>생년월일</th>
                  <th>직종</th>
                  <th>시급</th>
                  <th>일수</th>
                  <th>시간</th>
                  <th>기본급</th>
                  <!-- 회계처리용: 과세/비과세 분리 -->
                  <template v-if="reportType === 'accounting'">
                    <th v-if="showColumns.premiumTax">연야휴과세</th>
                    <th v-if="showColumns.premiumNontax" class="nontax-col">연야휴비과세</th>
                  </template>
                  <!-- 고객용: 합산 -->
                  <template v-else>
                    <th v-if="showClientColumns.overtimeTotal">연장수당</th>
                    <th v-if="showClientColumns.nightTotal">야간수당</th>
                    <th v-if="showClientColumns.holidayTotal">휴일수당</th>
                  </template>
                  <th v-if="showColumns.dayoff">연차</th>
                  <th v-if="showColumns.longterm">장기</th>
                  <th v-if="showColumns.sunim">선임</th>
                  <th v-if="showColumns.bonus">상여</th>
                  <th v-if="showColumns.edu">교육</th>
                  <th v-if="showColumns.bojon">보존</th>
                  <th v-if="showColumns.etc">기타</th>
                  <th v-if="showColumns.dang">당직</th>
                  <th v-if="showColumns.lunch" class="nontax-col">{{ reportType === 'client' ? COMMON_WAGE_CLIENT_NONTAX_LABELS.lunch : '중식비(비과세)' }}</th>
                  <th v-if="showColumns.car" class="nontax-col">{{ reportType === 'client' ? COMMON_WAGE_CLIENT_NONTAX_LABELS.car : '차량(비과세)' }}</th>
                  <th v-if="showColumns.childcare" class="nontax-col">{{ reportType === 'client' ? COMMON_WAGE_CLIENT_NONTAX_LABELS.child : '양육(비과세)' }}</th>
                  <th class="total-col">임금합계</th>
                  <!-- 회계처리용만 조정내역 표시 -->
                  <template v-if="reportType === 'accounting'">
                    <th>과세</th>
                    <th>통상</th>
                  </template>
                  <th v-if="showColumns.lYeon">국민연금</th>
                  <th v-if="showColumns.lGun">건강보험</th>
                  <th v-if="showColumns.lJang">장기요양</th>
                  <th v-if="showColumns.lGo">고용보험</th>
                  <th v-if="showColumns.lSo">소득세</th>
                  <th v-if="showColumns.lJi">지방세</th>
                  <th v-if="showColumns.lJiwon">지원공제</th>
                  <th v-if="showColumns.lSik">식비공제</th>
                  <th v-if="showColumns.lEtc">기타공제</th>
                  <th v-if="showColumns.lYearend">연말정산</th>
                  <th class="total-col">공제합계</th>
                  <th class="net-col">실수령액</th>
                  <th v-if="showColumns.cYeon">국민연금</th>
                  <th v-if="showColumns.cGun">건강보험</th>
                  <th v-if="showColumns.cJang">장기요양</th>
                  <th v-if="showColumns.cGo">고용보험</th>
                  <th v-if="showColumns.cSil">실업급여</th>
                  <th v-if="showColumns.cSan">산재보험</th>
                  <th v-if="showColumns.cOut">퇴직금</th>
                  <th class="total-col">기관합계</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in sortedData" :key="row.emp_match_key || `${row.ce_name}|${row.ce_birth_date || ''}`" class="data-row">
                  <td class="center">{{ index + 1 }}</td>
                  <td class="center">{{ row.ce_name }}</td>
                  <td class="center">{{ getBirthDateStr(row) }}</td>
                  <td class="center">{{ getJobType(row) }}</td>
                  <td class="right">{{ formatNumber(row.cwhd_pay_calc_hourly) }}</td>
                  <td class="right">{{ formatDecimal(row.cwhd_work_days) }}</td>
                  <td class="right">{{ formatDecimal(row.cwhd_work_hours) }}</td>
                  <td class="right">{{ formatNumber(row.cwhd_basic) }}</td>
                  <!-- 회계처리용: 과세/비과세 분리 -->
                  <template v-if="reportType === 'accounting'">
                    <td v-if="showColumns.premiumTax" class="right">{{ formatNumber(getCommonWagePremiumTaxTotal(row)) }}</td>
                    <td v-if="showColumns.premiumNontax" class="right nontax-col">{{ formatNumber(getCommonWagePremiumNontaxTotal(row)) }}</td>
                  </template>
                  <!-- 고객용: 합산 -->
                  <template v-else>
                    <td v-if="showClientColumns.overtimeTotal" class="right">{{ formatNumber(getOvertimeTotal(row)) }}</td>
                    <td v-if="showClientColumns.nightTotal" class="right">{{ formatNumber(getNightTotal(row)) }}</td>
                    <td v-if="showClientColumns.holidayTotal" class="right">{{ formatNumber(getHolidayTotal(row)) }}</td>
                  </template>
                  <td v-if="showColumns.dayoff" class="right">{{ formatNumber(row.cwhd_dayoff) }}</td>
                  <td v-if="showColumns.longterm" class="right">{{ formatNumber(row.cwhd_senior) }}</td>
                  <td v-if="showColumns.sunim" class="right">{{ formatNumber(row.cwhd_sunim) }}</td>
                  <td v-if="showColumns.bonus" class="right">{{ formatNumber(row.cwhd_bonus) }}</td>
                  <td v-if="showColumns.edu" class="right">{{ formatNumber(row.cwhd_bosu) }}</td>
                  <td v-if="showColumns.bojon" class="right">{{ formatNumber(row.cwhd_bojon) }}</td>
                  <td v-if="showColumns.etc" class="right">{{ formatNumber(row.cwhd_other) }}</td>
                  <td v-if="showColumns.dang" class="right">{{ formatNumber(row.cwhd_dang) }}</td>
                  <td v-if="showColumns.lunch" class="right nontax-col">{{ formatNumber(row.cwhd_lunch) }}</td>
                  <td v-if="showColumns.car" class="right nontax-col">{{ formatNumber(row.cwhd_car) }}</td>
                  <td v-if="showColumns.childcare" class="right nontax-col">{{ formatNumber(row.cwhd_child) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': rowWageMismatch(row) }"
                    :title="rowWageMismatch(row) ? '항목 합과 임금합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(getRowGrossPay(row)) }}</td>
                  <!-- 회계처리용만 조정내역 표시 -->
                  <template v-if="reportType === 'accounting'">
                    <td
                      class="right"
                      :class="{ 'total-mismatch': rowTaxableMismatch(row) }"
                      :title="rowTaxableMismatch(row) ? '항목 기준 과세와 저장 과세가 일치하지 않습니다' : undefined"
                    >{{ formatNumber(row.cwhd_taxable) }}</td>
                    <td class="right">{{ formatNumber(row.cwhd_usual) }}</td>
                  </template>
                  <td v-if="showColumns.lYeon" class="right">{{ formatNumber(row.cwhd_l_yeon) }}</td>
                  <td v-if="showColumns.lGun" class="right">{{ formatNumber(row.cwhd_l_gun) }}</td>
                  <td v-if="showColumns.lJang" class="right">{{ formatNumber(row.cwhd_l_jang) }}</td>
                  <td v-if="showColumns.lGo" class="right">{{ formatNumber(row.cwhd_l_go) }}</td>
                  <td v-if="showColumns.lSo" class="right">{{ formatNumber(row.cwhd_l_so) }}</td>
                  <td v-if="showColumns.lJi" class="right">{{ formatNumber(row.cwhd_l_ji) }}</td>
                  <td v-if="showColumns.lJiwon" class="right">{{ formatNumber(row.cwhd_l_jiwon) }}</td>
                  <td v-if="showColumns.lSik" class="right">{{ formatNumber(row.cwhd_l_sik) }}</td>
                  <td v-if="showColumns.lEtc" class="right">{{ formatNumber(row.cwhd_l_etc) }}</td>
                  <td v-if="showColumns.lYearend" class="right">{{ formatNumber(row.cwhd_l_yearend) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': rowLaborMismatch(row) }"
                    :title="rowLaborMismatch(row) ? '근로자공제 항목 합과 공제합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(row.cwhd_l_total) }}</td>
                  <td class="right net-col">{{ formatNumber(getRowNetPay(row)) }}</td>
                  <td v-if="showColumns.cYeon" class="right">{{ formatNumber(row.cwhd_c_yeon) }}</td>
                  <td v-if="showColumns.cGun" class="right">{{ formatNumber(row.cwhd_c_gun) }}</td>
                  <td v-if="showColumns.cJang" class="right">{{ formatNumber(row.cwhd_c_jang) }}</td>
                  <td v-if="showColumns.cGo" class="right">{{ formatNumber(row.cwhd_c_go) }}</td>
                  <td v-if="showColumns.cSil" class="right">{{ formatNumber(row.cwhd_c_sil) }}</td>
                  <td v-if="showColumns.cSan" class="right">{{ formatNumber(row.cwhd_c_san) }}</td>
                  <td v-if="showColumns.cOut" class="right">{{ formatNumber(row.cwhd_c_out) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': rowCorpMismatch(row) }"
                    :title="rowCorpMismatch(row) ? '기관공제 항목 합과 기관합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(row.cwhd_c_total) }}</td>
                </tr>
                <tr class="total-row">
                  <td class="center" colspan="4">합계</td>
                  <td class="right" colspan="3"></td>
                  <td class="right">{{ formatNumber(totals.base) }}</td>
                  <!-- 회계처리용: 과세/비과세 분리 -->
                  <template v-if="reportType === 'accounting'">
                    <td v-if="showColumns.premiumTax" class="right">{{ formatNumber(totals.premiumTax) }}</td>
                    <td v-if="showColumns.premiumNontax" class="right nontax-col">{{ formatNumber(totals.premiumNontax) }}</td>
                  </template>
                  <!-- 고객용: 합산 -->
                  <template v-else>
                    <td v-if="showClientColumns.overtimeTotal" class="right">{{ formatNumber(totals.overtimeTotal) }}</td>
                    <td v-if="showClientColumns.nightTotal" class="right">{{ formatNumber(totals.nightTotal) }}</td>
                    <td v-if="showClientColumns.holidayTotal" class="right">{{ formatNumber(totals.holidayTotal) }}</td>
                  </template>
                  <td v-if="showColumns.dayoff" class="right">{{ formatNumber(totals.dayoff) }}</td>
                  <td v-if="showColumns.longterm" class="right">{{ formatNumber(totals.longterm) }}</td>
                  <td v-if="showColumns.sunim" class="right">{{ formatNumber(totals.sunim) }}</td>
                  <td v-if="showColumns.bonus" class="right">{{ formatNumber(totals.bonus) }}</td>
                  <td v-if="showColumns.edu" class="right">{{ formatNumber(totals.edu) }}</td>
                  <td v-if="showColumns.bojon" class="right">{{ formatNumber(totals.bojon) }}</td>
                  <td v-if="showColumns.etc" class="right">{{ formatNumber(totals.etc) }}</td>
                  <td v-if="showColumns.dang" class="right">{{ formatNumber(totals.dang) }}</td>
                  <td v-if="showColumns.lunch" class="right">{{ formatNumber(totals.lunch) }}</td>
                  <td v-if="showColumns.car" class="right">{{ formatNumber(totals.car) }}</td>
                  <td v-if="showColumns.childcare" class="right">{{ formatNumber(totals.childcare) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': footerMismatch.wage }"
                    :title="footerMismatch.wage ? '임금 항목 합과 임금합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(totals.total) }}</td>
                  <!-- 회계처리용만 조정내역 표시 -->
                  <template v-if="reportType === 'accounting'">
                    <td
                      class="right"
                      :class="{ 'total-mismatch': footerMismatch.taxable }"
                      :title="footerMismatch.taxable ? '과세 합계가 일치하지 않습니다' : undefined"
                    >{{ formatNumber(totals.taxable) }}</td>
                    <td class="right">{{ formatNumber(totals.usual) }}</td>
                  </template>
                  <td v-if="showColumns.lYeon" class="right">{{ formatNumber(totals.lYeon) }}</td>
                  <td v-if="showColumns.lGun" class="right">{{ formatNumber(totals.lGun) }}</td>
                  <td v-if="showColumns.lJang" class="right">{{ formatNumber(totals.lJang) }}</td>
                  <td v-if="showColumns.lGo" class="right">{{ formatNumber(totals.lGo) }}</td>
                  <td v-if="showColumns.lSo" class="right">{{ formatNumber(totals.lSo) }}</td>
                  <td v-if="showColumns.lJi" class="right">{{ formatNumber(totals.lJi) }}</td>
                  <td v-if="showColumns.lJiwon" class="right">{{ formatNumber(totals.lJiwon) }}</td>
                  <td v-if="showColumns.lSik" class="right">{{ formatNumber(totals.lSik) }}</td>
                  <td v-if="showColumns.lEtc" class="right">{{ formatNumber(totals.lEtc) }}</td>
                  <td v-if="showColumns.lYearend" class="right">{{ formatNumber(totals.lYearend) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': footerMismatch.labor }"
                    :title="footerMismatch.labor ? '근로자공제 항목 합과 공제합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(totals.lTotal) }}</td>
                  <td class="right net-col">{{ formatNumber(totals.netPay) }}</td>
                  <td v-if="showColumns.cYeon" class="right">{{ formatNumber(totals.cYeon) }}</td>
                  <td v-if="showColumns.cGun" class="right">{{ formatNumber(totals.cGun) }}</td>
                  <td v-if="showColumns.cJang" class="right">{{ formatNumber(totals.cJang) }}</td>
                  <td v-if="showColumns.cGo" class="right">{{ formatNumber(totals.cGo) }}</td>
                  <td v-if="showColumns.cSil" class="right">{{ formatNumber(totals.cSil) }}</td>
                  <td v-if="showColumns.cSan" class="right">{{ formatNumber(totals.cSan) }}</td>
                  <td v-if="showColumns.cOut" class="right">{{ formatNumber(totals.cOut) }}</td>
                  <td
                    class="right total-col"
                    :class="{ 'total-mismatch': footerMismatch.corp }"
                    :title="footerMismatch.corp ? '기관공제 항목 합과 기관합계가 일치하지 않습니다' : undefined"
                  >{{ formatNumber(totals.cTotal) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.wage-report-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wage-report-modal {
  background: white;
  border-radius: 8px;
  width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 8px 8px 0 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .download-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: #10b981;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s;
    min-width: 120px;
    justify-content: center;

    &:hover:not(:disabled) {
      background: #059669;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }

    .spin {
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    color: #374151;

    &:hover {
      background: #e5e7eb;
      border-color: #9ca3af;
      color: #111827;
    }
  }
}

.modal-body {
  flex: 1;
  overflow: auto;
  padding: 16px;
}

.table-wrapper {
  overflow-x: auto;
}

.wage-report-table {
  width: max-content;
  border-collapse: collapse;
  font-size: 11px;

  th, td {
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  .header-row-1 th {
    background: #1e3a5f;
    color: white;
    font-weight: 600;
    text-align: center;
    padding: 8px;
  }

  .header-row-2 th {
    background: #e5e7eb;
    font-weight: 600;
    text-align: center;
  }

  .section-basic { background: #1e3a5f; }
  .section-work { background: #2563eb; }
  .section-wage { background: #059669; }
  .section-adjust { background: #7c3aed; }
  .section-emp-deduct { background: #dc2626; }
  .section-center-deduct { background: #d97706; }
  .section-net { background: #0891b2; }

  .nontax-col {
    background: #e0f2fe !important;
    color: #0e7490;
  }

  .total-col {
    background: #fef3c7 !important;
    font-weight: 600;
  }

  .total-mismatch {
    color: #c62828 !important;
    font-weight: 700;
  }

  .net-col {
    background: #dcfce7 !important;
    font-weight: 600;
    color: #166534;
  }

  .data-row {
    td {
      background: white;
    }

    &:hover td {
      background: #f9fafb;
    }
  }

  .total-row {
    td {
      background: #fef3c7 !important;
      font-weight: 600;
      border-top: 2px solid #1e3a5f;
    }
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }
}
</style>
